import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface FunProgressProps {
  percent: number;
  tool?: string;
  className?: string;
}

const MESSAGES: Record<string, string[]> = {
  youtube: [
    '🎵 Bribing YouTube servers...',
    '🚀 Downloading at the speed of light...',
    '🎶 Extracting the vibes...',
    '🤫 Shh, YouTube doesn\'t know...',
    '🎧 Almost got the beats...',
    '🏃 Running faster than your WiFi...',
    '✨ Almost done, quack quack!',
    '🎉 Just wrapping it up...',
  ],
  video: [
    '🎬 Talking to ffmpeg nicely...',
    '⚡ Converting at warp speed...',
    '🔧 Tightening the bolts...',
    '🎞️ Splicing the frames...',
    '🍿 Almost ready to watch...',
    '🚀 Turbo mode activated!',
    '✨ Almost done, hold tight!',
    '🎉 Finishing touches...',
  ],
  audio: [
    '🎵 Tuning the frequencies...',
    '🎸 Shredding the audio...',
    '🎤 Mic check, 1 2 3...',
    '🔊 Cranking up the volume...',
    '🎧 Mixing the beats...',
    '✨ Almost done, quack quack!',
    '🎉 Just a sec more...',
  ],
  instagram: [
    '📸 Sneaking past Instagram...',
    '🕵️ Going incognito...',
    '🤙 Sliding into the DMs...',
    '📱 Downloading the drip...',
    '✨ Almost got it!',
  ],
  transcribe: [
    '🎙️ Listening very carefully...',
    '🧠 AI is thinking hard...',
    '📝 Writing it all down...',
    '🤔 Decoding the words...',
    '✨ Almost transcribed!',
    '🎉 Wrapping up the text...',
  ],
  upload: [
    '📤 Sending your file to space...',
    '🚀 Uploading at light speed...',
    '📡 Beaming it up...',
    '⚡ Almost there...',
  ],
  default: [
    '⚡ Working on it...',
    '🔧 Doing the magic...',
    '🚀 Almost there...',
    '✨ Just a moment...',
    '🎉 Nearly done, quack quack!',
    '🏁 Final stretch!',
  ],
};

const FunProgress: React.FC<FunProgressProps> = ({ percent, tool = 'default', className = '' }) => {
  const [msgIndex, setMsgIndex] = useState(0);
  const messages = MESSAGES[tool] || MESSAGES.default;
  const clamped = Math.min(Math.max(percent, 0), 100);

  // Cycle through messages every 2.5 seconds
  useEffect(() => {
    if (percent >= 100) return;
    const interval = setInterval(() => {
      setMsgIndex(i => (i + 1) % messages.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [percent, messages.length]);

  // Pick message based on progress
  const progressMsg = percent >= 95 ? messages[messages.length - 1]
    : percent >= 70 ? messages[Math.min(messages.length - 2, Math.floor(messages.length * 0.7))]
    : messages[msgIndex];

  const gradient = clamped >= 100
    ? 'from-emerald-400 to-teal-500'
    : 'from-[#216974] via-indigo-500 to-blue-500';

  return (
    <div className={`w-full ${className}`}>
      <div className="flex items-center justify-between mb-2.5 gap-2">
        <AnimatePresence mode="wait">
          <motion.span
            key={progressMsg}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.3 }}
            className="text-xs font-medium text-gray-600 truncate"
          >
            {progressMsg}
          </motion.span>
        </AnimatePresence>
        <span className="text-xs font-bold text-[#216974] tabular-nums shrink-0">
          {Math.round(clamped)}%
        </span>
      </div>

      <div className="relative w-full h-2.5 bg-gray-100 rounded-full overflow-hidden shadow-inner">
        <motion.div
          className={`absolute inset-y-0 left-0 bg-gradient-to-r rounded-full ${gradient}`}
          initial={{ width: 0 }}
          animate={{ width: `${clamped}%` }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          {clamped < 100 && clamped > 0 && (
            <div className="absolute inset-0 rounded-full overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default FunProgress;
